import { useMemo, useCallback } from 'react';
import { useApiClient, useFetchInfinite } from '@marageti/z4-lib';
import { ApiEvent, EventsFilters } from '../types';
import { DEFAULT_LIMIT } from '../constants';

export const useApiEvents = (filters: EventsFilters) => {
  const apiClient = useApiClient();
  const limit = filters.limit || DEFAULT_LIMIT;

  const getKey = useCallback(
    (pageIndex: number, previousPageData: ApiEvent[] | null) => {
      if (previousPageData && previousPageData.length < limit) return null;
      const offset = previousPageData?.length
        ? previousPageData[previousPageData.length - 1].id
        : undefined;
      return ['getApiEvents', filters.day, filters.entity, filters.logLevel, limit, offset];
    },
    [filters.day, filters.entity, filters.logLevel, limit],
  );

  const fetcher = useCallback(
    ([, day, entity, logLevel, lim, offset]: (string | number | undefined)[]) => (
      apiClient.trackingClient.getApiEvents({
        params: {
          day, entity, log_level: logLevel, limit: lim, offset,
        },
      })
    ),
    [apiClient],
  );

  const {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useFetchInfinite<ApiEvent[]>(getKey, fetcher, {
    revalidateFirstPage: false,
    revalidateOnFocus: false,
  });

  const events = useMemo(() => data?.flat() ?? [], [data]);

  const lastPage = data?.[data.length - 1];
  const hasMore = !lastPage || lastPage.length >= limit;

  const loadMore = useCallback(() => {
    if (!isValidating) setSize(size + 1);
  }, [isValidating, setSize, size]);

  const fetchEvent = useCallback(
    async (id: string): Promise<ApiEvent | null> => {
      try {
        return await apiClient.trackingClient.getApiEvent(id);
      } catch {
        return null;
      }
    },
    [apiClient],
  );

  return {
    events,
    isLoading: isLoading || isValidating,
    error: error ?? null,
    hasMore,
    fetchEvent,
    loadMore,
  };
};

export default useApiEvents;
